import {
    Business as BusinessIcon,
    Menu as MenuIcon,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [servicesAnchor, setServicesAnchor] = useState<null | HTMLElement>(null);
    const location = useLocation();

    const menuItems = [
        { label: 'Home', path: '/' },
        { label: 'About', path: '/about' },
        { label: 'Services', path: '/services', hasDropdown: true },
        { label: 'Contact', path: '/contact' },
    ];

    const services = [
        { label: 'Strategy Consulting', path: '/services#strategy' },
        { label: 'Digital Transformation', path: '/services#digital' },
        { label: 'Operations Excellence', path: '/services#operations' },
        { label: 'Change Management', path: '/services#change' },
    ];

    const handleServicesClick = (event: React.MouseEvent<HTMLElement>) => {
        setServicesAnchor(event.currentTarget);
    };

    const handleServicesClose = () => {
        setServicesAnchor(null);
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const isActiveRoute = (path: string) => {
        return location.pathname === path;
    };

    return (
        <AppBar
            position="fixed"
            elevation={4}
            sx={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            }}
        >
            <Toolbar sx={{ py: 1 }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: isMobile ? 1 : 0 }}>
                    <BusinessIcon sx={{ mr: 1.5, fontSize: 28, color: '#1565c0' }} />
                    <Typography
                        variant="h6"
                        component={Link}
                        to="/"
                        sx={{
                            fontWeight: 300,
                            fontSize: '1.4rem',
                            color: '#2c3e50',
                            textDecoration: 'none',
                            mr: 6,
                            fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                            letterSpacing: '0.5px',
                        }}
                    >
                        AI Solutions
                    </Typography>
                </Box>

                {/* Desktop Menu */}
                {!isMobile && (
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                        {menuItems.map((item) => (
                            <React.Fragment key={item.label}>
                                {item.hasDropdown ? (
                                    <>
                                        <Button
                                            onClick={handleServicesClick}
                                            sx={{
                                                mx: 2,
                                                fontSize: '0.95rem',
                                                fontWeight: 400,
                                                textTransform: 'none',
                                                color: '#2c3e50',
                                                fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                                                letterSpacing: '0.3px',
                                                backgroundColor: isActiveRoute(item.path!) ? 'rgba(21, 101, 192, 0.08)' : 'transparent',
                                                borderRadius: '6px',
                                                px: 2.5,
                                                py: 1,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(21, 101, 192, 0.08)',
                                                    color: '#1565c0',
                                                },
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                        <Menu
                                            anchorEl={servicesAnchor}
                                            open={Boolean(servicesAnchor)}
                                            onClose={handleServicesClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                            elevation={3}
                                            sx={{
                                                '& .MuiPaper-root': {
                                                    borderRadius: '8px',
                                                    mt: 1,
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                }
                                            }}
                                        >
                                            {services.map((service) => (
                                                <MenuItem
                                                    key={service.label}
                                                    component={Link}
                                                    to={service.path}
                                                    onClick={handleServicesClose}
                                                    sx={{
                                                        fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                                                        fontSize: '0.9rem',
                                                        fontWeight: 400,
                                                        py: 1.5,
                                                        px: 3,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(21, 101, 192, 0.08)',
                                                        }
                                                    }}
                                                >
                                                    {service.label}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                ) : (
                                    <Button
                                        component={Link}
                                        to={item.path!}
                                        sx={{
                                            mx: 2,
                                            fontSize: '0.95rem',
                                            fontWeight: 400,
                                            textTransform: 'none',
                                            color: '#2c3e50',
                                            fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                                            letterSpacing: '0.3px',
                                            backgroundColor: isActiveRoute(item.path!) ? 'rgba(21, 101, 192, 0.08)' : 'transparent',
                                            borderRadius: '6px',
                                            px: 2.5,
                                            py: 1,
                                            '&:hover': {
                                                backgroundColor: 'rgba(21, 101, 192, 0.08)',
                                                color: '#1565c0',
                                            },
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                )}
                            </React.Fragment>
                        ))}
                    </Box>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                    <IconButton
                        edge="end"
                        aria-label="menu"
                        onClick={toggleMobileMenu}
                        sx={{ color: '#2c3e50' }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                {/* Contact Button (Desktop) */}
                {!isMobile && (
                    <Button
                        variant="contained"
                        component={Link}
                        to="/contact"
                        sx={{
                            backgroundColor: '#1565c0',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 400,
                            fontSize: '0.95rem',
                            fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                            borderRadius: '6px',
                            px: 3,
                            py: 1,
                            boxShadow: '0 2px 8px rgba(21, 101, 192, 0.2)',
                            '&:hover': {
                                backgroundColor: '#0d47a1',
                                boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)',
                            },
                        }}
                    >
                        Get Started
                    </Button>
                )}
            </Toolbar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={toggleMobileMenu}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 280,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 20px rgba(0,0,0,0.1)',
                    },
                }}
            >
                <List sx={{ pt: 4 }}>
                    {menuItems.map((item) => (
                        <ListItem
                            key={item.label}
                            component={Link}
                            to={item.path!}
                            onClick={toggleMobileMenu}
                            sx={{
                                backgroundColor: isActiveRoute(item.path!) ? 'rgba(21, 101, 192, 0.08)' : 'transparent',
                                mx: 2,
                                borderRadius: '6px',
                                mb: 1,
                            }}
                        >
                            <ListItemText
                                primary={item.label}
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontWeight: 400,
                                        fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                                        color: '#2c3e50',
                                    }
                                }}
                            />
                        </ListItem>
                    ))}
                    <ListItem sx={{ px: 2, mt: 2 }}>
                        <Button
                            variant="contained"
                            component={Link}
                            to="/contact"
                            fullWidth
                            onClick={toggleMobileMenu}
                            sx={{
                                backgroundColor: '#1565c0',
                                textTransform: 'none',
                                fontWeight: 400,
                                fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                                borderRadius: '6px',
                                py: 1.5,
                                boxShadow: '0 2px 8px rgba(21, 101, 192, 0.2)',
                            }}
                        >
                            Get Started
                        </Button>
                    </ListItem>
                </List>
            </Drawer>
        </AppBar>
    );
};

export default Navbar;